import React from 'react';
import { FormState } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CreateJobDto } from '../../../../../../services/applicant_tracking/JobService/CreateJobDto';
import {
  SetupHeaderButtonType,
  SetupPageHeader,
} from '../../../../../../components/SetupPageHeader';
import {
  SetupFooterButtonType,
  SetupPageFooter,
} from '../../../../../../components/SetupPageFooter';
import { replaceFinalSegment } from '../../../../../../utils/url';
import { useStateMachine } from 'little-state-machine';
import { JobDetails } from '../../../../../../services/applicant_tracking/JobService/EditJobDto';

interface PropTypes {
  setupStep: number;
  jobDetails: JobDetails;
  formState: FormState<CreateJobDto>;
  setSetupStep: (newStep: number, jobDTO: CreateJobDto) => void;
  onCancel: () => void;
  onSave: () => void;
  onNavigate: () => void;
  isEditFlow: boolean;
  isLoading: boolean;
  children: React.ReactNode;
}

export function ApplicationFormLayout(props: PropTypes) {
  const navigate = useNavigate();
  const { state } = useStateMachine();
  const isLoadingOrFormInvalid = props.isLoading || !props.formState.isValid;

  const goToPrevious = () => {
    props.onNavigate();
    props.setSetupStep(props.setupStep - 1, state.setupJob);
    navigate(replaceFinalSegment('job_post'));
  };

  const goToNext = () => {
    props.onNavigate();
    props.setSetupStep(props.setupStep + 1, state.setupJob);
    navigate(replaceFinalSegment('job_workflow'));
  };

  const previousButton: SetupHeaderButtonType = {
    title: 'Previous',
    type: 'Navigate',
    action: goToPrevious,
    disabled: isLoadingOrFormInvalid,
  };

  const nextButton: SetupHeaderButtonType = {
    title: 'Next',
    type: 'Navigate',
    action: goToNext,
    disabled: isLoadingOrFormInvalid,
  };

  const saveButtonText =
    props.isEditFlow && props.jobDetails?.status === 'published'
      ? 'Save and Publish'
      : 'Save and Continue';

  const saveAsDraft: SetupFooterButtonType = {
    title: 'Save as Draft',
    type: 'Save',
    action: props.onSave,
    disabled: isLoadingOrFormInvalid,
  };

  const saveAndContinue: SetupFooterButtonType = {
    title: saveButtonText,
    type: 'Save',
    disabled: isLoadingOrFormInvalid,
  };

  const firstItems = [previousButton];
  if (props.isEditFlow) {
    firstItems.push(nextButton);
  }

  return (
    <>
      <SetupPageHeader
        title='Application Form'
        firstItems={firstItems}
        intermediateItems={[
          {
            title: 'Cancel',
            type: 'Cancel',
            action: props.onCancel,
          },
          {
            title: saveButtonText,
            type: 'Save',
            disabled: isLoadingOrFormInvalid,
          },
        ]}
      />
      {props.children}
      {!props.isLoading && (
        <SetupPageFooter
          classNames='mt-4'
          firstItem={saveAndContinue}
          lastItem={props.isEditFlow ? null : saveAsDraft}
        />
      )}
    </>
  );
}
